import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError } from '@awj/api-client';
import { formatDate, formatDateTime, formatMoney, formatNumber } from '@awj/i18n';
import { Button, Dialog, Skeleton, useToast } from '@awj/ui';

import { ExportMenu } from '~/components/ExportMenu.tsx';
import { FieldHead } from '~/components/InfoHint.tsx';
import { PageTitle } from '~/components/PageTitle.tsx';
import { useI18n } from '~/i18n/index.tsx';
import {
  useBlockCustomer,
  useCustomer,
  useDeleteCustomer,
  useResendConfirmation,
} from '~/lib/customer-queries.ts';
import { customerOrderColumns } from '~/lib/export-columns.ts';
import { useSession } from '~/lib/session.tsx';

/**
 * One customer: what they have bought, what they have said, and where they wanted it sent.
 *
 * The two things staff can do here are deliberately unequal. Blocking is reversible and keeps
 * everything — it is the answer to almost every problem. Deleting exists for accounts that never
 * became customers, is SuperAdmin-only, and the API refuses it the moment an order exists.
 */
export function CustomerDetailPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const { has } = useSession();
  const { id: idParam } = useParams();

  const id = Number(idParam);
  const customer = useCustomer(id);
  const block = useBlockCustomer(id);
  const resend = useResendConfirmation(id);
  const remove = useDeleteCustomer();

  const [blocking, setBlocking] = useState(false);
  const [reason, setReason] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (customer.isPending) {
    return (
      <>
        <PageTitle title={t('customers.customer')} />
        <Skeleton height="420px" />
      </>
    );
  }

  if (customer.isError) {
    return (
      <>
        <PageTitle title={t('customers.customer')} />
        <div className="empty-panel">
          <p>{t('customers.notFound')}</p>
          <Button variant="secondary" onClick={() => navigate('/customers')}>
            {t('nav.customers')}
          </Button>
        </div>
      </>
    );
  }

  const data = customer.data;

  async function unblock() {
    try {
      await block.mutateAsync({ blocked: false });
      toast.show({ title: t('customers.unblocked'), tone: 'success' });
    } catch (error) {
      toast.show({
        title: t('customers.couldNotChange'),
        message: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'danger',
      });
    }
  }

  async function resendConfirmation() {
    try {
      await resend.mutateAsync();
      toast.show({ title: t('customers.confirmationSent', { email: data.email }), tone: 'success' });
    } catch (error) {
      toast.show({
        title: t('customers.couldNotSendConfirmation'),
        message: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'danger',
      });
    }
  }

  return (
    <>
      <PageTitle
        title={data.fullName || data.email}
        back="/customers"
        actions={
          <>
            {/*
              A customer whose link expired cannot sign in, and cannot register again either.
              This is the way back that staff can offer when they get in touch.
            */}
            {!data.emailConfirmed && !data.isBlocked ? (
              <Button type="button" variant="secondary" loading={resend.isPending} onClick={resendConfirmation}>
                {t('customers.resendConfirmation')}
              </Button>
            ) : null}

            {data.isBlocked ? (
              <Button type="button" variant="secondary" loading={block.isPending} onClick={unblock}>
                {t('customers.unblock')}
              </Button>
            ) : (
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setReason('');
                  setBlocking(true);
                }}
              >
                {t('customers.block')}
              </Button>
            )}

            {/*
              Only offered when it can actually work: SuperAdmin, and an account with no orders
              behind it. Anything else and the API would refuse — better not to ask.
            */}
            {/* Quiet in the header; it turns red in the dialog that confirms it. */}
            {has('SuperAdmin') && data.orderCount === 0 ? (
              <Button
                type="button"
                variant="ghost"
                className="awj-button--quietDanger"
                onClick={() => setConfirmDelete(true)}
              >
                {t('common.delete')}
              </Button>
            ) : null}
          </>
        }
      />

      <div className="order-head">
        {data.isBlocked ? (
          <span className="status status--cancelled">{t('customers.blocked')}</span>
        ) : (
          <span className="status status--active">{t('customers.active')}</span>
        )}
        {!data.emailConfirmed ? (
          <span className="status status--draft">{t('customers.unconfirmed')}</span>
        ) : null}
        <span className="order-head__meta">
          {t('customers.registeredAt', { at: formatDate(locale, data.registeredAt) })}
        </span>
        {data.blockedAt ? (
          <span className="order-head__meta">
            {t('customers.blockedAt', { at: formatDateTime(locale, data.blockedAt) })}
          </span>
        ) : null}
      </div>

      <div className="editor">
        <div className="editor__main">
          <section className="card" aria-labelledby="orders-title">
            <div className="card__head">
              <h2 className="section-label" id="orders-title">
                {t('customers.orders')}
              </h2>
              {data.orders.length > 0 ? (
                <ExportMenu
                  file={`customer-${data.id}-orders`}
                  sheet={t('customers.orders')}
                  title={t('export.ordersFor', { name: data.fullName || data.email })}
                  columns={customerOrderColumns(t, locale)}
                  rows={() => data.orders}
                  variant="ghost"
                />
              ) : null}
            </div>

            {data.orders.length === 0 ? (
              <p className="field__hint">{t('customers.neverOrdered')}</p>
            ) : (
              <div className="data-table__scroll" tabIndex={0} role="region" aria-label={t('a11y.scrollableTable')}>
                <table className="data-table__table">
                  <thead>
                    <tr>
                      <th className="data-table__th" scope="col">{t('orders.number')}</th>
                      <th className="data-table__th" scope="col">{t('orders.placed')}</th>
                      <th className="data-table__th" scope="col">{t('orders.status')}</th>
                      <th scope="col" className="data-table__th data-table__cell--end">
                        {t('orders.items')}
                      </th>
                      <th scope="col" className="data-table__th data-table__cell--end">
                        {t('orders.total')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.orders.map((order) => (
                      <tr key={order.number}>
                        <td className="data-table__td">
                          <Link className="data-table__link mono" to={`/orders/${order.number}`}>
                            {order.number}
                          </Link>
                        </td>
                        <td className="data-table__td">
                          <bdi>{formatDate(locale, order.placedAt)}</bdi>
                        </td>
                        <td className="data-table__td">
                          <span className={`status status--${order.status.toLowerCase()}`}>
                            {t(`orderStatus.${order.status}` as 'orderStatus.Paid')}
                          </span>
                        </td>
                        <td className="data-table__td data-table__cell--end">
                          <bdi>{formatNumber(locale, order.itemCount)}</bdi>
                        </td>
                        <td className="data-table__td data-table__cell--end">
                          <bdi>{formatMoney(locale, order.grandTotal)}</bdi>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* The detail carries the latest orders only; the rest are one filtered list away. */}
            {data.orderCount > data.orders.length ? (
              <p className="field__hint">
                {t('customers.latestOrders', {
                  shown: formatNumber(locale, data.orders.length),
                  total: formatNumber(locale, data.orderCount),
                })}{' '}
                <Link to={`/orders?q=${encodeURIComponent(data.email)}`}>{t('customers.allOrders')}</Link>
              </p>
            ) : null}
          </section>

          <section className="card" aria-labelledby="reviews-title">
            <h2 className="section-label" id="reviews-title">
              {t('customers.reviews')}
            </h2>

            {data.reviews.length === 0 ? (
              <p className="field__hint">{t('customers.noReviews')}</p>
            ) : (
              <ul className="review-list">
                {data.reviews.map((review) => (
                  <li className="review-list__item" key={review.id}>
                    <div>
                      <p className="review-list__product">
                        <bdi>{locale === 'ar' ? review.productNameAr : review.productNameEn}</bdi>
                      </p>
                      {review.title ? (
                        <p className="review-list__title">
                          <bdi>{review.title}</bdi>
                        </p>
                      ) : null}
                      <p className="review-list__meta">
                        <bdi>{formatDate(locale, review.createdAt)}</bdi>
                      </p>
                    </div>
                    <div className="review-list__end">
                      {/* Stars rather than "4/5": it is read at a glance, next to four others. */}
                      <span className="review-list__rating" aria-label={t('customers.rating', { rating: String(review.rating) })}>
                        <span aria-hidden="true">{'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}</span>
                      </span>
                      <span className={`status status--${review.status.toLowerCase()}`}>
                        {t(`reviewStatus.${review.status}` as 'reviewStatus.Approved')}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <aside className="editor__side">
          <section className="card" aria-labelledby="contact-title">
            <h2 className="section-label" id="contact-title">
              {t('customers.contact')}
            </h2>
            <p className="detail-line detail-line--strong">
              <bdi>{data.fullName}</bdi>
            </p>
            <p className="detail-line">
              <a href={`mailto:${data.email}`}>
                <bdi>{data.email}</bdi>
              </a>
            </p>
            {data.phoneNumber ? (
              <p className="detail-line">
                <a href={`tel:${data.phoneNumber}`} dir="ltr">
                  {data.phoneNumber}
                </a>
              </p>
            ) : null}
            <p className="detail-line detail-line--quiet">
              {t('customers.writesIn', {
                language: data.preferredLocale === 'ar' ? t('lang.arabic') : t('lang.english'),
              })}
            </p>
          </section>

          <section className="card" aria-labelledby="spend-title">
            <h2 className="section-label" id="spend-title">
              {t('customers.spend')}
            </h2>
            <dl className="totals">
              <div>
                <dt>{t('customers.orders')}</dt>
                <dd>{formatNumber(locale, data.orderCount)}</dd>
              </div>
              <div>
                <dt>{t('customers.spent')}</dt>
                <dd>{formatMoney(locale, data.totalSpent)}</dd>
              </div>
              {data.lastOrderAt ? (
                <div>
                  <dt>{t('customers.lastOrder')}</dt>
                  <dd>{formatDate(locale, data.lastOrderAt)}</dd>
                </div>
              ) : null}
            </dl>
          </section>

          <section className="card" aria-labelledby="addresses-title">
            <h2 className="section-label" id="addresses-title">
              {t('customers.addresses')}
            </h2>

            {data.addresses.length === 0 ? (
              <p className="field__hint">{t('customers.noAddresses')}</p>
            ) : (
              data.addresses.map((address) => (
                <div className="address-block" key={address.id}>
                  <p className="detail-line detail-line--strong">
                    <bdi>{address.fullName}</bdi>
                    {address.isDefault ? (
                      <span className="address-block__badge">{t('customers.defaultAddress')}</span>
                    ) : null}
                  </p>
                  <p className="detail-line">
                    <bdi>{address.line1}</bdi>
                  </p>
                  {address.line2 ? (
                    <p className="detail-line">
                      <bdi>{address.line2}</bdi>
                    </p>
                  ) : null}
                  <p className="detail-line">
                    <bdi>{address.city}</bdi>
                    {address.emirate !== address.city ? <bdi>{`, ${address.emirate}`}</bdi> : null}
                  </p>
                  <p className="detail-line" dir="ltr">
                    {address.phoneNumber}
                  </p>
                </div>
              ))
            )}
          </section>
        </aside>
      </div>

      <Dialog
        open={blocking}
        onClose={() => setBlocking(false)}
        title={t('customers.blockTitle')}
        closeLabel={t('common.close')}
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setBlocking(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant="danger"
              loading={block.isPending}
              onClick={async () => {
                try {
                  await block.mutateAsync({ blocked: true, reason });
                  setBlocking(false);
                  toast.show({ title: t('customers.blockedNow'), tone: 'success' });
                } catch (error) {
                  toast.show({
                    title: t('customers.couldNotChange'),
                    message: error instanceof ApiError ? error.problem.detail : undefined,
                    tone: 'danger',
                  });
                }
              }}
            >
              {t('customers.block')}
            </Button>
          </>
        }
      >
        <div className="transition-form">
          <p className="dialog-lede">{t('customers.blockExplain')}</p>
          <div className="field">
            {/* Internal. The customer is told only that they cannot sign in. */}
            <FieldHead
              htmlFor="block-reason"
              label={t('customers.reason')}
              hint={t('customers.reasonHint')}
              hintId="block-reason-hint"
            />
            <textarea
              id="block-reason"
              className="awj-textarea"
              rows={3}
              maxLength={500}
              value={reason}
              aria-describedby="block-reason-hint"
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={t('customers.deleteTitle')}
        closeLabel={t('common.close')}
        size="sm"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setConfirmDelete(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant="danger"
              loading={remove.isPending}
              onClick={async () => {
                try {
                  await remove.mutateAsync(id);
                  toast.show({ title: t('customers.deleted'), tone: 'success' });
                  navigate('/customers');
                } catch (error) {
                  toast.show({
                    title: t('customers.couldNotDelete'),
                    message: error instanceof ApiError ? error.problem.detail : undefined,
                    tone: 'danger',
                  });
                }
              }}
            >
              {t('common.delete')}
            </Button>
          </>
        }
      >
        <p className="dialog-lede">{t('customers.deleteExplain')}</p>
      </Dialog>
    </>
  );
}
